import { inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";

import { MutationOptionsAlone } from "apollo-angular/types";
import { catchError, exhaustMap, filter, interval, map, mergeMap, of, switchMap, takeUntil, tap } from "rxjs";

import { generateMutationContext } from "src/app/core/auth/session";

import { BulkEditCalendarEventGQL, BulkEditCalendarEventMutationVariables, CalendarEvent_LocationFragment, EditCalendarEventInput, JobDetailsV2Fragment, UpdateJobGQL, UpdateJobMutation, UpdateJobMutationVariables } from "../../../../../../generated/graphql.generated";
import { USER_DELETED_ALL_LOCATIONS_ATTRIBUTE } from "../../../../../global.constants";

import { FreyaNotificationsService } from "../../../../../services/freya-notifications.service";
import { createConfigSelector } from "../../../../../state/branding.store";
import { getResourceChanges } from "../../../../job-helpers";
import { selectJob, selectJobFranchiseId, selectJobId } from "../../../../job-tool.reducer";
import { BookEventDialogActions } from "../timeline-book-event.actions";
import { timelineBookEventFeature } from "../timeline-book-event.feature";
import { selectEventItineraryLocations, selectEventToBook, selectPlaceholderEvent } from "../timeline-book-event.selectors";

import { EventItineraryActions } from "./event-itinerary.actions";
import { eventItineraryFeature } from "./event-itinerary.feature";
import { saveEventChangesTriggers } from "./event-itinerary.reducers";
import { selectCanSaveLocations, selectLocationsOrderChanged } from "./event-itinerary.selectors";


export const reloadLocationData = createEffect((
   actions$ = inject(Actions),
   store = inject(Store),
) => {
     return actions$.pipe(
       ofType(EventItineraryActions.includeDockTravelTimeCheckboxToggled),
       concatLatestFrom(() => [
         store.select(selectJob),
         store.select(selectEventToBook),
       ]),
       map(([ _action, job, event ]) => EventItineraryActions.locationDataReloaded({
         job,
         event,
       })),
     );
   }, { functional: true, dispatch: true });

export const addLocationToJob = createEffect((
   actions$ = inject(Actions),
   store = inject(Store),
   updateJobGQL = inject(UpdateJobGQL),
) => {
     return actions$.pipe(
       ofType(EventItineraryActions.autocompleteLocationSelected),
       mergeMap((action) => of(action).pipe(
           concatLatestFrom(() => [
            store.select(selectJobId),
            store.select(selectJobFranchiseId),
           ]),
           map(([ { location, locationType }, jobId, jobFranchiseId ]): {
               vars: UpdateJobMutationVariables;
               opts: MutationOptionsAlone<UpdateJobMutation, UpdateJobMutationVariables>;
           } => ({
               vars: {
                   updateJobs: {
                       jobId,
                       addLocations: [
                           {
                               newLocationInput: location,
                               locationType,
                           }
                       ],
                   },
               },
               opts: {
                   context: generateMutationContext({
                       zone: jobFranchiseId,
                   }),
               },
           })),
           exhaustMap(({ vars, opts }) => updateJobGQL.mutate(vars, opts).pipe(
               map((res) => {
                   const updatedJob = res.data.updateJobs.jobs[0];
                   const jobLocation = updatedJob.locations.find((l) => l.locationType === action.locationType);

                   return {
                       updatedJob,
                       location: jobLocation.location,
                       locationId: action.locationId,
                   }
               }),
               map((payload) => EventItineraryActions.addLocationToJobSuccess(payload)),
           )),
           catchError((error) => of(EventItineraryActions.addLocationToJobError({ error }))),
       )),
     );
   }, { functional: true, dispatch: true });

// Start timer when location order is changed, location is selected or location is deleted
export const startCountdown = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
        return actions$.pipe(
            ofType(
                EventItineraryActions.locationDropped,
                EventItineraryActions.dropdownLocationSelected,
                EventItineraryActions.deleteLocationButtonClicked,
                EventItineraryActions.autocompleteLocationSelected,
                EventItineraryActions.addLocationToJobSuccess,
                EventItineraryActions.locationDataReloaded,
            ),
            // Start the timer when a new location is selected that is not a pending location (not yet saved to DB)
            filter((action) => {
                if (action.type === EventItineraryActions.dropdownLocationSelected.type) {
                    return Boolean(action?.location.originalLocation);
                }
                return true;
            }),
            concatLatestFrom(() => [
                store.select(createConfigSelector('CALENDAREVENTS_AUTOSAVEEVENTTIMER')),
                store.select(selectLocationsOrderChanged),
                store.select(selectCanSaveLocations),
            ]),
            filter(([_action, _zoneIntitialCountdown, hasChanges, canSave ]) => hasChanges && canSave),
            map(([ _action, zoneInitialCountdown ]) => zoneInitialCountdown || 10),
                map((initialCountdown) => EventItineraryActions.countdownStarted({ initialCountdown })),
        );
    },
    { functional: true, dispatch: true },
);

export const stopCountdown = createEffect((
    actions$ = inject(Actions),
) => {
        return actions$.pipe(
            ofType(
              ...saveEventChangesTriggers,
              EventItineraryActions.dropdownLocationSelected,
              EventItineraryActions.addStopButtonClicked,
              BookEventDialogActions.closeBookDialogButtonClicked,
              BookEventDialogActions.bookButtonClicked,
            ),
            // Stop the timer when a new location is selected that is a pending location (not yet saved to DB)
            filter((action) => {
                if (action.type === EventItineraryActions.dropdownLocationSelected.type) {
                    return !action?.location.originalLocation;
                }
                return true;
            }),
            map(() => EventItineraryActions.countdownStopped()),
        );
}, { functional: true, dispatch: true });

export const decrementCountdown = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
      ofType(EventItineraryActions.countdownStarted),
      switchMap(() => interval(1000).pipe(
        takeUntil(actions$.pipe(ofType(EventItineraryActions.countdownStopped))),
        concatLatestFrom(() => store.select(eventItineraryFeature.selectEventItinerary)),
        map(([ _action, { saveEventCountdown } ]) => {
            if (saveEventCountdown > 1) {
                return EventItineraryActions.countdownTicked();
            } else {
                return EventItineraryActions.countdownCompleted();
            }
        }),
      ),
    ),
    );
    },
{ functional: true, dispatch: true },
);

export const notifyCannotSaveChanges = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
    notify = inject(FreyaNotificationsService),
) => {
    return actions$.pipe(
      ofType(...saveEventChangesTriggers),
      concatLatestFrom(() => store.select(selectCanSaveLocations)),
      filter(([ _action, canSave ]) => !canSave),
      tap(() => notify.error('Cannot save event changes, please assign all new locations')),
    );
}, { functional: true, dispatch: false });

export const saveEventChanges = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    bulkEditCalendarEventGQL = inject(BulkEditCalendarEventGQL),
  ) => {
    return actions$.pipe(
      ofType(...saveEventChangesTriggers),
      switchMap((action) => of(action).pipe(
          concatLatestFrom(() => [
              store.select(selectCanSaveLocations),
              store.select(timelineBookEventFeature.selectBookEventDialog),
              store.select(selectEventToBook),
              store.select(selectPlaceholderEvent),
              store.select(selectJob),
              store.select(selectEventItineraryLocations)
          ]),
          filter(([ _action, canSave ]) => canSave),
          tap(() => store.dispatch(EventItineraryActions.saveEventChangesStarted())),
          map(([ _action, _canSave, { assetIds }, originalEvent, eventEdit, job, locations ]): {
              job: JobDetailsV2Fragment;
              vars: BulkEditCalendarEventMutationVariables;
          } => {

              const { added: addLocations, removed: removeLocations, edited: editLocations } =
                  getResourceChanges<CalendarEvent_LocationFragment>({
                  original: originalEvent.locations,
                  modified: locations as unknown as CalendarEvent_LocationFragment[],
                  keyExtractor: (item) => `${item.type}-${item.id}`,
                    compareFn: (original, modified) =>
                      original.order === modified.order &&
                      original.estimatedTimeAtLocation === modified.estimatedTimeAtLocation &&
                      original.travelTimeToNextLocationOffset === modified.travelTimeToNextLocationOffset,
                  detectEdited: true
              });

              const edit: EditCalendarEventInput = {
                  setAssets: {
                      addAssets: assetIds,
                      removeAssets: eventEdit.assets?.map((a) => a.id)
                  },
                  setLocations: {
                      addLocations: addLocations.map((location) => ({
                          estimatedTimeAtLocation: location.estimatedTimeAtLocation,
                          travelTimeToNextLocationOffset: location.travelTimeToNextLocationOffset,
                          ignoreInDuration: false,
                          locationId: location?.location?.id,
                          order: location.order,
                          type: location.type,
                      })),
                      editLocations: editLocations.map((location) => ({
                          id: location.id,
                          estimatedTimeAtLocation: location.estimatedTimeAtLocation,
                          travelTimeToNextLocationOffset: location.travelTimeToNextLocationOffset,
                          ignoreInDuration: false,
                          order: location.order,
                          type: location.type,
                      })),
                      removeLocations: removeLocations.map((rl) => rl.id),
                  },
              }

              if (eventEdit.start) {
                  edit.start = eventEdit.start;
              }

              if (eventEdit.end) {
                  edit.end = eventEdit.end;
              }

              if (eventEdit.start && eventEdit.end) {
                  edit.modifyStartForTravel = true;
                  edit.modifyEndForTravel = true;
              }

              const userDeletedAllLocations = originalEvent.locations.length === 0 && locations.length > 0;

              const originalAttributes = originalEvent.attributes || [];

              if (userDeletedAllLocations) {
                  edit.attributes = [ ...originalAttributes, USER_DELETED_ALL_LOCATIONS_ATTRIBUTE ];
              } else {
                  edit.attributes = originalAttributes.filter((att) => att !== USER_DELETED_ALL_LOCATIONS_ATTRIBUTE );
              }

              return {
                  job,
                  vars: {
                    edits: [
                      {
                        id: eventEdit.id,
                        edit,
                      }
                    ],
                  }
              }
          }),
          exhaustMap(({ vars, job }) => bulkEditCalendarEventGQL.mutate(vars).pipe(
              map((res) => EventItineraryActions.saveEventChangesSuccess({
                  job,
                  jobId: job.id,
                  updatedEvent: res.data.bulkEditCalendarEvent.events[0],
                  promptedBy: action,
              })),
          )),
          catchError((error) => of(EventItineraryActions.saveEventChangesError({ error }))),
      )),
    );
  },
  { functional: true, dispatch: true },
);
